import { models } from "types/api/viewModels.ts";
import {
	CabinetworksGroupText,
	CustomQuoteDeclinedStatusDescription,
	CustomQuoteExpiredStatusDescription,
	CustomQuoteReadyToOrderStatusDescription,
	CustomQuoteRecivedStatusDescription,
	CustomQuoteUnderReviewStatusDescription,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { convertToTitleCase } from "utils/string.ts";
import { displayDesigner } from "utils/order";
import { CustomQuoteStatus, UserRole } from "types/declarations/enums.ts";
import { QuoteFilterOpts } from "pages/CustomQuote/CustomQuoteLandingPage/CustomQuoteLandingPage.tsx";
import { format } from "date-fns";

export const CustomQuoteStatusDescription = {
	[CustomQuoteStatus.Received]: CustomQuoteRecivedStatusDescription,
	[CustomQuoteStatus.UnderReview]: CustomQuoteUnderReviewStatusDescription,
	[CustomQuoteStatus.ReadyToOrder]: CustomQuoteReadyToOrderStatusDescription,
	[CustomQuoteStatus.Declined]: CustomQuoteDeclinedStatusDescription,
	[CustomQuoteStatus.Expired]: CustomQuoteExpiredStatusDescription
};

export const formatQuoteStatus = (status: models["CustomQuoteStatus"] | undefined): string => {
	const newStatus = status?.replace(/([A-Z])/g, " $1").trim();
	return convertToTitleCase(newStatus) ?? UnavailableDataPlaceholderText;
};

export const getProductLine = (customQuote: models["CustomQuoteViewModel"]): string | undefined =>
	customQuote?.globals?.productLine?.description ?? undefined;

export const getAssigneeName = (customQuote: models["CustomQuoteViewModel"]) => {
	return (
		(customQuote?.assignedTo?.firstName &&
			customQuote?.assignedTo?.lastName &&
			`${customQuote?.assignedTo?.firstName} ${customQuote?.assignedTo?.lastName}`) ??
		UnavailableDataPlaceholderText
	);
};

export const isCompletedRequestStatus = (request: models["CustomQuoteViewModel"]) => {
	return (
		request.status === CustomQuoteStatus.Expired ||
		request.status === CustomQuoteStatus.Declined ||
		(request.status === CustomQuoteStatus.ReadyToOrder && request.responsibleParty !== UserRole.QUOTER)
	);
};

const getDraftValue = (item: models["CustomQuoteViewModel"], key: string) => {
	switch (key) {
		case "designerNumber":
			return item?.designer?.designerNumber || "Designer not identified";
		case "productLine":
			return item?.globals?.productLine?.description ?? "";
		case "status":
			return formatQuoteStatus(item?.status) ?? "";
		default:
			return "";
	}
};

const getCleanFilterValues = (key: string, selectedFilterValues: string[]): string[] => {
	return selectedFilterValues.map((fv: string) => {
		if (fv === "Designer not identified") {
			return "--";
		}
		fv = fv.trim();
		return fv.toUpperCase();
	});
};

export const compareFilterValues = (
	key: string,
	selectedFilterValues: string[],
	item: models["CustomQuoteViewModel"]
) => {
	const cleanFilterValues = getCleanFilterValues(key, selectedFilterValues);

	// Special handling for designer number to match the display format used in filters
	if (key === "designerNumber") {
		const { designer } = item;
		const formattedDesigner = displayDesigner(designer);
		return cleanFilterValues.some((filterValue) => formattedDesigner?.toUpperCase() === filterValue.toUpperCase());
	}

	const draftedValues = getDraftValue(item, key);

	// Handle array values (for future extensibility)
	if (Array.isArray(draftedValues)) {
		return draftedValues.some((value) => cleanFilterValues.includes(value?.trim()));
	}

	// Standard string comparison
	return cleanFilterValues.includes(draftedValues.toUpperCase());
};

export const getFilteredQuotes = (
	filterOptions: QuoteFilterOpts,
	quotes: models["CustomQuoteViewModel"][]
): models["CustomQuoteViewModel"][] => {
	const { designers, productLines, status } = filterOptions;

	const filterEntries = Object.entries({
		designerNumber: designers,
		productLine: productLines,
		status: status
	}).filter((x) => x[1].length > 0);

	if (filterEntries.length === 0) return quotes;

	return quotes.filter((item) => filterEntries.every(([key, values]) => compareFilterValues(key, values, item)));
};

export const getRevisionIdNumbers = (requests: models["CustomQuoteViewModel"][] | undefined): string[] | undefined => {
	if (!requests) return undefined;

	return requests.flatMap(
		(request) => request?.revisions?.map((revision) => revision?.revisionId?.toString() ?? "").filter(Boolean) ?? []
	);
};

export const sortByTimestampDesc = <T extends { timestamp?: string }>(arr: T[]): T[] =>
	[...arr].sort((a, b) => {
		const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
		const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
		return dateB - dateA;
	});

export const getRequestNames = (requests: models["CustomQuoteViewModel"][] | undefined): string[] | undefined =>
	requests?.map((request) => request?.requestName ?? "").filter(Boolean);

export const getCommentDisplayName = (
	quote: models["CustomQuoteViewModel"] | undefined,
	comment: models["CustomQuoteCommentViewModel"]
) => {
	if (comment.userRole === UserRole.QUOTER) {
		return CabinetworksGroupText;
	}

	// For USER role, use the designer's name from the quote
	if (comment.designer?.firstName && comment.designer?.lastName) {
		return `${comment.designer.firstName} ${comment.designer.lastName}`;
	}

	if (quote?.designer?.firstName && quote?.designer?.lastName) {
		return `${quote.designer.firstName} ${quote.designer?.lastName}`;
	}

	if (quote?.requestedBy?.firstName && quote?.requestedBy?.lastName) {
		return `${quote.requestedBy?.firstName} ${quote.requestedBy?.lastName}`;
	}

	// Fallback to the userRole if we can't determine the name
	return comment.userRole;
};

const DATE_PLACEHOLDER = `Sent on ${UnavailableDataPlaceholderText} at ${UnavailableDataPlaceholderText}`;
export const formatTimestamp = (timestamp?: string): string => {
	if (!timestamp) return DATE_PLACEHOLDER;
	try {
		const timestampDate: Date = new Date(timestamp!);
		if (timestampDate.toString() === "Invalid Date") {
			return DATE_PLACEHOLDER;
		}
		return `Sent on ${format(timestampDate, "MM/dd/yy")} at ${format(timestampDate, "hh:mm a")}`;
	} catch (e) {
		return DATE_PLACEHOLDER;
	}
};

export const formatQuoteDesignerName = (quote?: models["CustomQuoteViewModel"]): string => {
	return displayDesigner(quote?.designer) ?? quote?.designerId ?? UnavailableDataPlaceholderText;
};
